import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import FadeIn from "./FadeIn";
import { useLang } from "../../contexts/LanguageContext";
import { useLocalized } from "../../lib/localized";
import { useCategories } from "../../lib/useCategories";
import { imageUrl } from "../../lib/api";

export default function ProjectsGrid({ content, projects }) {
  const { t, lang } = useLang();
  const lp = useLocalized();
  const [active, setActive] = useState("all");
  const { categories } = useCategories();

  const filtered = useMemo(() => {
    if (active === "all") return projects;
    return projects.filter((p) => p.category === active);
  }, [active, projects]);

  const allLabel = t(content?.projects_section?.filters?.all) || (lang === "id" ? "Semua" : "All");

  return (
    <section
      id="projects"
      className="container-x py-24 md:py-32 hairline-t"
      data-testid="projects-section"
    >
      <FadeIn>
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
          <div>
            <div className="eyebrow mb-6">{t(content?.projects_section?.eyebrow)}</div>
            <h2
              className="font-display font-bold text-[#16181c] text-4xl sm:text-5xl md:text-6xl leading-[1.02]"
              data-testid="projects-heading"
            >
              {t(content?.projects_section?.heading)}
            </h2>
          </div>
          <Link
            to={lp("/proyek", "/en/projects")}
            className="text-[15px] font-medium text-[#16181c] border-b-2 border-[#16181c] pb-1 hover:text-[#5d5f64] hover:border-[#5d5f64]"
            data-testid="projects-view-all"
          >
            {t(content?.projects_section?.view_all)}
          </Link>
        </div>

        <div
          className="flex md:flex-wrap gap-3 mb-12 overflow-x-auto no-scrollbar -mx-6 px-6 md:mx-0 md:px-0 md:overflow-visible scroll-smooth"
          data-testid="projects-filters"
        >
          {[{ slug: "all", name: { id: allLabel, en: allLabel } }, ...categories].map((cat) => {
            const isActive = active === cat.slug;
            return (
              <button
                key={cat.slug}
                type="button"
                onClick={() => setActive(cat.slug)}
                data-testid={`filter-chip-${cat.slug}`}
                className={`shrink-0 whitespace-nowrap px-5 py-2 rounded-full text-[14px] font-medium border transition-colors ${
                  isActive
                    ? "bg-[#16181c] text-white border-[#16181c]"
                    : "bg-white text-[#16181c] border-[#e3e3e1] hover:bg-[#f3f3f0]"
                }`}
              >
                {t(cat.name)}
              </button>
            );
          })}
        </div>
      </FadeIn>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16"
        data-testid="projects-grid"
      >
        {filtered.map((p, idx) => (
          <FadeIn key={p.id} delay={Math.min(idx, 5) * 60}>
            <Link
              to={lp(`/proyek/${p.slug}`, `/en/projects/${p.slug}`)}
              className="group block"
              data-testid={`project-tile-${p.slug}`}
            >
              <div className="relative aspect-[4/5] w-full bg-[#1b1c1e] overflow-hidden rounded-2xl">
                {p.cover_image ? (
                  <img
                    src={imageUrl(p.cover_image)}
                    alt={t(p.title || p.name)}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700 ease-out"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1b1c1e] to-[#2a2c30]" />
                )}
                <div className="absolute top-4 left-4 text-white/80 text-xs font-medium tracking-wide">
                  {String(idx + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="mt-5">
                <div className="eyebrow text-[#5d5f64] mb-2">
                  {(() => {
                    const c = categories.find((x) => x.slug === p.category);
                    return c ? t(c.name) : p.category;
                  })()}
                </div>
                <div className="flex items-start justify-between gap-6">
                  <h3
                    className="font-display font-semibold text-[#16181c] text-xl md:text-2xl leading-tight tracking-[-0.01em]"
                    lang={lang}
                  >
                    {t(p.title || p.name)}
                  </h3>
                  <div className="text-[13px] text-[#5d5f64] whitespace-nowrap pt-1">
                    {p.location} · {p.year}
                  </div>
                </div>
              </div>
            </Link>
          </FadeIn>
        ))}

        {filtered.length === 0 && (
          <div className="md:col-span-3 py-20 text-center text-[#5d5f64]">
            {lang === "id" ? "Belum ada proyek pada kategori ini." : "No projects in this category yet."}
          </div>
        )}
      </div>
    </section>
  );
}
